import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  operationsApi,
  type AssetDetail,
  type DocumentDto,
  type FuelCostRollupRow,
  type FuelLog,
} from '../api';
import { SmartLoading } from '@/lib/ui';
import { DocumentUploadModal } from '../components/DocumentUploadModal';
import { FuelLogDetailModal } from '../components/FuelLogDetailModal';

type Tab = 'profile' | 'documents' | 'service' | 'fuel' | 'stats' | 'tickets' | 'alerts';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'profile', label: 'Profile' },
  { key: 'documents', label: 'Documents' },
  { key: 'service', label: 'Service History' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'stats', label: 'Operational Stats' },
  { key: 'tickets', label: 'Open Tickets' },
  { key: 'alerts', label: 'Alerts' },
];

const colorClass: Record<DocumentDto['status_color'], string> = {
  ok: 'bg-emerald-500/15 text-emerald-300',
  warning: 'bg-amber-500/15 text-amber-300',
  expiring: 'bg-orange-500/15 text-orange-300',
  expired: 'bg-rose-500/15 text-rose-300',
};

export function AssetDetailPage() {
  const { assetId = '' } = useParams<{ assetId: string }>();
  const [detail, setDetail] = useState<AssetDetail | null>(null);
  const [fuel, setFuel] = useState<FuelLog[]>([]);
  const [tab, setTab] = useState<Tab>('profile');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFuel, setSelectedFuel] = useState<FuelLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, f] = await Promise.all([
        operationsApi.getAsset(assetId),
        operationsApi.listFuelForAsset(assetId),
      ]);
      setDetail(d);
      setFuel(f);
    } catch (e) {
      setError((e as Error).message || 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assetId) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  if (loading) return <SmartLoading className="p-6 text-sm text-muted-foreground" />;
  if (error) return <div className="p-6 text-sm text-rose-300">{error}</div>;
  if (!detail) return null;

  const { asset, documents, service_history, open_tickets, open_alerts } = detail;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-4">
        <div className="text-xs text-muted-foreground">
          <Link to="/assets" className="hover:underline">
            Assets
          </Link>{' '}
          / <span className="font-mono">{asset.code}</span>
        </div>
        <div className="mt-1 flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{asset.name}</h1>
          <span className="rounded bg-muted px-2 py-0.5 text-xs">{asset.status}</span>
          {asset.deleted_at && (
            <span className="rounded bg-rose-500/15 px-2 py-0.5 text-xs text-rose-300">
              DELETED
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
          <Kpi label="Open alerts" value={open_alerts} />
          <Kpi
            label="30-day fuel"
            value={`${(detail.total_fuel_minor_30d / 100).toLocaleString()} ${asset.currency ?? ''}`}
          />
          <Kpi label="30-day liters" value={detail.total_fuel_liters_30d.toFixed(1) + ' L'} />
          <Kpi label="Service ops" value={service_history.length} />
        </div>
      </div>

      <div className="flex gap-1 border-b border-border px-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              'border-b-2 px-3 py-2 text-sm transition-colors ' +
              (tab === t.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground')
            }
          >
            {t.label}
            {t.key === 'documents' && documents.length > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">({documents.length})</span>
            )}
            {t.key === 'tickets' && open_tickets.length > 0 && (
              <span className="ml-2 rounded bg-rose-500/20 px-1 text-xs text-rose-300">
                {open_tickets.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {tab === 'profile' && <ProfileTab asset={asset} />}
        {tab === 'documents' && (
          <DocumentsTab
            documents={documents}
            onAddClick={() => setShowUpload(true)}
            onDelete={async (id) => {
              await operationsApi.deleteDocument(id);
              await refresh();
            }}
          />
        )}
        {tab === 'service' && <ServiceTab history={service_history} />}
        {tab === 'fuel' && <FuelTab logs={fuel} onSelect={setSelectedFuel} />}
        {tab === 'stats' && (
          <OperationalStatsTab assetId={asset.id} currency={asset.currency ?? 'AED'} />
        )}
        {tab === 'tickets' && <TicketsTab tickets={open_tickets} />}
        {tab === 'alerts' && (
          <p className="text-sm text-muted-foreground">
            {open_alerts} open alert{open_alerts === 1 ? '' : 's'}. Use the Command Center alert
            feed for the full list.
          </p>
        )}
      </div>

      {showUpload && (
        <DocumentUploadModal
          scope="ASSET"
          subjectId={asset.id}
          onClose={() => setShowUpload(false)}
          onUploaded={async () => {
            setShowUpload(false);
            await refresh();
          }}
        />
      )}

      {selectedFuel && (
        <FuelLogDetailModal
          log={selectedFuel}
          onClose={() => setSelectedFuel(null)}
          onVerified={(updated) => {
            setFuel((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
            setSelectedFuel(updated);
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded border border-border bg-surface px-3 py-2">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-lg tabular-nums">{value}</div>
    </div>
  );
}

function ProfileTab({ asset }: { asset: AssetDetail['asset'] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
      <Row k="Code" v={asset.code} mono />
      <Row k="Type" v={asset.asset_type_slug} />
      <Row k="Plate" v={asset.plate_number ?? '—'} mono />
      <Row k="Status" v={asset.status} />
      <Row k="Make" v={asset.make ?? '—'} />
      <Row k="Model" v={asset.model ?? '—'} />
      <Row k="Year" v={asset.year ?? '—'} />
      <Row k="VIN" v={asset.vin ?? '—'} mono />
      <Row k="Fuel type" v={asset.fuel_type ?? '—'} />
      <Row
        k="Capacity"
        v={
          asset.capacity_value != null
            ? `${asset.capacity_value} ${asset.capacity_unit ?? ''}`
            : '—'
        }
      />
      <Row
        k="Odometer"
        v={asset.odometer_current != null ? `${asset.odometer_current.toLocaleString()} km` : '—'}
      />
      <Row
        k="Purchase"
        v={asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString() : '—'}
      />
      {asset.notes && (
        <div className="col-span-2 mt-3 rounded border border-border bg-muted/30 px-3 py-2 text-sm">
          {asset.notes}
        </div>
      )}
    </dl>
  );
}

function Row({ k, v, mono }: { k: string; v: string | number; mono?: boolean }) {
  return (
    <div className="flex justify-between border-b border-border/50 py-1.5">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={mono ? 'font-mono' : ''}>{v}</dd>
    </div>
  );
}

function DocumentsTab({
  documents,
  onAddClick,
  onDelete,
}: {
  documents: DocumentDto[];
  onAddClick: () => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {documents.length} document{documents.length === 1 ? '' : 's'}
        </p>
        <button className="btn-primary" onClick={onAddClick}>
          + Upload document
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-4">Title</th>
            <th className="py-2 pr-4">Type</th>
            <th className="py-2 pr-4">Issued</th>
            <th className="py-2 pr-4">Expires</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {documents.map((d) => (
            <tr key={d.id} className="border-b border-border/60 hover:bg-muted/20">
              <td className="py-2 pr-4">{d.title}</td>
              <td className="py-2 pr-4 text-muted-foreground">{d.document_type_slug ?? '—'}</td>
              <td className="py-2 pr-4 text-xs text-muted-foreground">
                {d.issued_on ? new Date(d.issued_on).toLocaleDateString() : '—'}
              </td>
              <td className="py-2 pr-4 text-xs text-muted-foreground">
                {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'}
              </td>
              <td className="py-2 pr-4">
                <span className={'rounded px-2 py-0.5 text-xs ' + colorClass[d.status_color]}>
                  {d.status_color}
                </span>
              </td>
              <td className="py-2 text-right text-xs">
                {d.download_url && (
                  <a
                    href={d.download_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mr-3 text-primary hover:underline"
                  >
                    View
                  </a>
                )}
                <button className="text-rose-300 hover:underline" onClick={() => onDelete(d.id)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {documents.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                No documents on file.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ServiceTab({ history }: { history: AssetDetail['service_history'] }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-muted-foreground">
        <tr className="border-b border-border">
          <th className="py-2 pr-4">Title</th>
          <th className="py-2 pr-4">Status</th>
          <th className="py-2 pr-4">Due</th>
          <th className="py-2 pr-4">Completed</th>
          <th className="py-2 pr-4">Parts</th>
          <th className="py-2 pr-4">Labor</th>
          <th className="py-2 pr-4">Odometer</th>
        </tr>
      </thead>
      <tbody>
        {history.map((w) => (
          <tr key={w.id} className="border-b border-border/60">
            <td className="py-2 pr-4">{w.title}</td>
            <td className="py-2 pr-4">{w.status}</td>
            <td className="py-2 pr-4 text-xs text-muted-foreground">
              {w.due_at ? new Date(w.due_at).toLocaleDateString() : '—'}
            </td>
            <td className="py-2 pr-4 text-xs text-muted-foreground">
              {w.completed_at ? new Date(w.completed_at).toLocaleDateString() : '—'}
            </td>
            <td className="py-2 pr-4 tabular-nums">
              {(w.parts_cost_minor / 100).toFixed(2)} {w.currency}
            </td>
            <td className="py-2 pr-4 tabular-nums">
              {(w.labor_cost_minor / 100).toFixed(2)} {w.currency}
            </td>
            <td className="py-2 pr-4 tabular-nums">
              {w.odometer_at_service?.toLocaleString() ?? '—'}
            </td>
          </tr>
        ))}
        {history.length === 0 && (
          <tr>
            <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
              No service history.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function FuelTab({ logs, onSelect }: { logs: FuelLog[]; onSelect: (log: FuelLog) => void }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-muted-foreground">
        <tr className="border-b border-border">
          <th className="py-2 pr-4">Logged</th>
          <th className="py-2 pr-4">Liters</th>
          <th className="py-2 pr-4">Cost</th>
          <th className="py-2 pr-4">Odometer</th>
          <th className="py-2 pr-4">Anomaly</th>
          <th className="py-2 pr-4">Verified</th>
          <th className="py-2"></th>
        </tr>
      </thead>
      <tbody>
        {logs.map((f) => (
          <tr
            key={f.id}
            className="cursor-pointer border-b border-border/60 hover:bg-muted/20"
            onClick={() => onSelect(f)}
          >
            <td className="py-2 pr-4 text-xs">{new Date(f.logged_at).toLocaleString()}</td>
            <td className="py-2 pr-4 tabular-nums">{f.liters.toFixed(1)}</td>
            <td className="py-2 pr-4 tabular-nums">
              {(f.cost_minor / 100).toFixed(2)} {f.currency}
            </td>
            <td className="py-2 pr-4 tabular-nums">{f.odometer?.toLocaleString() ?? '—'}</td>
            <td className="py-2 pr-4">
              {f.is_anomaly ? (
                <span title={f.anomaly_reason ?? ''} className="text-amber-300">
                  ⚠ anomaly
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="py-2 pr-4">
              {f.verified_at ? (
                <span className="text-emerald-300">✓</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="py-2 text-right text-xs">
              <button className="text-primary hover:underline">View</button>
            </td>
          </tr>
        ))}
        {logs.length === 0 && (
          <tr>
            <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
              No fuel logs.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function OperationalStatsTab({ assetId, currency }: { assetId: string; currency: string }) {
  const [days, setDays] = useState(30);
  const [row, setRow] = useState<FuelCostRollupRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    operationsApi
      .fuelCostPerAsset({ asset_id: assetId, days })
      .then((res) => setRow(res.rows[0] ?? null))
      .catch((e) => setError((e as Error).message || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, [assetId, days]);

  const fuelTotal = row ? (row.total_cost_minor / 100).toFixed(2) : '0.00';
  const distance = row ? row.distance_km.toLocaleString() : '0';
  const costPerKm = row ? (row.cost_per_km_minor / 100).toFixed(3) : '0.000';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase text-muted-foreground">Window</span>
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      {error && <p className="text-sm text-rose-300">{error}</p>}

      <div className="grid grid-cols-3 gap-4">
        <Kpi label={`Fuel total (${currency})`} value={loading ? '…' : fuelTotal} />
        <Kpi label="Distance (km)" value={loading ? '…' : distance} />
        <Kpi label={`Cost / km (${currency})`} value={loading ? '…' : costPerKm} />
      </div>

      <p className="text-xs text-muted-foreground">
        Distance is the odometer span across fuel fills in the window. Cost/km is fuel spend ÷
        distance. Add more fuel logs with odometer readings to refine the estimate.
      </p>
    </div>
  );
}

function TicketsTab({ tickets }: { tickets: AssetDetail['open_tickets'] }) {
  return (
    <ul className="space-y-2">
      {tickets.map((t) => (
        <li key={t.id} className="rounded border border-border p-3">
          <div className="flex items-center justify-between">
            <div className="font-medium">{t.title}</div>
            <span
              className={
                'rounded px-2 py-0.5 text-xs ' +
                (t.severity === 'CRITICAL'
                  ? 'bg-rose-500/15 text-rose-300'
                  : t.severity === 'HIGH'
                    ? 'bg-orange-500/15 text-orange-300'
                    : 'bg-slate-500/15 text-slate-300')
              }
            >
              {t.severity}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>
          <div className="mt-2 text-xs text-muted-foreground">
            Raised {new Date(t.raised_at).toLocaleString()} · {t.status}
          </div>
        </li>
      ))}
      {tickets.length === 0 && (
        <p className="text-sm text-muted-foreground">No open maintenance tickets.</p>
      )}
    </ul>
  );
}
